import { createFileRoute, Link } from "@tanstack/react-router";
import { BadgeCheck, CheckCircle2, CreditCard, ShieldCheck, Wallet } from "lucide-react";

import { CarGrid } from "@/components/site/CarGrid";
import { Button } from "@/components/ui/button";
import { cars } from "@/data/cars";
import { hreflangLinks } from "@/lib/i18n";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/car-rental-no-deposit-bucharest";
const title = "Car Rental No Deposit Bucharest — No Credit Card";
const description =
  "Rent a car in Bucharest with no deposit and no credit card. Nothing blocked on your card, full insurance and rovinieta included, from €44/day.";

const faqs = [
  {
    q: "Do I need a deposit to rent a car in Bucharest with carentals.ro?",
    a: "No. We take zero security deposit. Nothing is frozen or pre-authorised on your card, and there is no hold released weeks after your rental ends.",
  },
  {
    q: "Do I need a credit card to book?",
    a: "No credit card is required. You can pay with a debit card, in cash (EUR or RON) at pickup, or by bank transfer for company and long-term rentals.",
  },
  {
    q: "Is insurance included in the no-deposit price?",
    a: "Yes. Full insurance (CASCO and RCA) and the rovinieta road vignette are included in the daily rate for every car in the fleet.",
  },
  {
    q: "What documents do I need for a no-deposit rental in Romania?",
    a: "A driving licence held for at least 12 months and a passport or national ID card. The minimum age is 21 (23 for the top luxury models).",
  },
  {
    q: "Is no-deposit car rental available at Otopeni Airport?",
    a: "Yes. Delivery and collection at Bucharest Henri Coandă (OTP) Airport is free, day or night, with the same no-deposit and no-credit-card policy.",
  },
];

export const Route = createFileRoute("/car-rental-no-deposit-bucharest")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      {
        name: "keywords",
        content:
          "car rental no deposit romania, car rental no deposit bucharest, rent a car without deposit bucharest, no credit card car rental romania, car hire no security deposit bucharest",
      },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/car-rental-no-deposit-bucharest", "/ro/inchirieri-auto-fara-garantie"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Car rental with no deposit in Bucharest",
          serviceType: "No deposit car rental",
          provider: { "@id": "https://carentals.ro/#organization" },
          areaServed: { "@type": "City", name: "Bucharest", addressCountry: "RO" },
          url: CANONICAL,
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: "44",
            availability: "https://schema.org/InStock",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        }),
      },
    ],
  }),
  component: NoDepositPage,
});

const pillars = [
  {
    icon: Wallet,
    title: "Zero security deposit",
    text: "No franchise hold, no cash guarantee, nothing frozen on your account before, during or after the rental.",
  },
  {
    icon: CreditCard,
    title: "No credit card required",
    text: "Debit card, cash in EUR or RON, or bank transfer for companies. We never pre-authorise a card.",
  },
  {
    icon: ShieldCheck,
    title: "Full insurance included",
    text: "CASCO and RCA cover plus the rovinieta road vignette are part of the daily price, not a counter upsell.",
  },
  {
    icon: BadgeCheck,
    title: "The price you see is final",
    text: "No airport surcharge, no young-driver trap, no mandatory extras added at handover.",
  },
];

function NoDepositPage() {
  const fleet = cars.slice(0, 8);

  return (
    <>
      <section className="bg-ink text-ink-foreground">
        <div className="container-page py-20">
          <p className="eyebrow">No deposit · No credit card · Bucharest &amp; Romania</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.08] sm:text-5xl">
            Car rental with no deposit in Bucharest, Romania
          </h1>
          <p className="mt-5 max-w-2xl text-base text-ink-foreground/80 sm:text-lg">
            carentals.ro rents cars in Bucharest without a security deposit and without a credit
            card. Nothing is blocked on your card — you pay the rental price, insurance and the road
            vignette are included, and delivery in the city and at Otopeni Airport is free. Cars
            from €44 per day.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/rentals">Book with zero deposit</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground"
            >
              <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">WhatsApp us</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((pillar) => (
            <div
              key={pillar.title}
              className="rounded-xl border border-border bg-card p-6 shadow-card"
            >
              <pillar.icon className="size-6 text-gold" />
              <h2 className="mt-4 font-display text-lg font-bold">{pillar.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{pillar.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="max-w-3xl">
          <p className="eyebrow">The difference</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            No deposit car hire vs. the standard Romanian rental
          </h2>
        </div>
        <div className="mt-10 overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-5 py-4 font-semibold">What you pay</th>
                <th className="px-5 py-4 font-semibold">Typical agency in Bucharest</th>
                <th className="px-5 py-4 font-semibold text-gold">carentals.ro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["Security deposit", "€300–€1,500 blocked for up to 30 days", "€0 — nothing blocked"],
                ["Credit card", "Mandatory, in the main driver's name", "Not required — debit or cash"],
                ["Insurance", "Basic cover, excess reduction sold at the desk", "Full CASCO + RCA included"],
                ["Rovinieta", "Often charged separately", "Included on every car"],
                ["Airport pickup", "€5–€15 surcharge", "Free at Otopeni (OTP)"],
                ["City delivery", "Rarely offered", "Free in Bucharest"],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-5 py-4 font-semibold">{row[0]}</td>
                  <td className="px-5 py-4 text-muted-foreground">{row[1]}</td>
                  <td className="px-5 py-4 font-semibold text-gold">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="max-w-3xl">
          <p className="eyebrow">Fleet</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Cars available in Bucharest without a deposit
          </h2>
          <p className="mt-3 text-muted-foreground">
            Every car below is bookable with zero deposit and no credit card, insurance and vignette
            included.
          </p>
        </div>
        <div className="mt-10">
          <CarGrid cars={fleet} />
        </div>
        <div className="mt-10">
          <Button asChild variant="gold" size="lg">
            <Link to="/rentals">See all 28 cars</Link>
          </Button>
        </div>
      </section>

      <section className="container-page pb-24">
        <div className="max-w-3xl">
          <p className="eyebrow">FAQ</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            No deposit car rental in Romania — your questions
          </h2>
          <dl className="mt-8 space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="rounded-xl border border-border bg-card p-6">
                <dt className="flex gap-3 font-display text-base font-bold">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gold" />
                  {faq.q}
                </dt>
                <dd className="mt-2 pl-7 text-sm text-muted-foreground">{faq.a}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-8 text-sm text-muted-foreground">
            Paying without plastic?{" "}
            <Link
              to="/rent-a-car-without-credit-card-bucharest"
              className="font-semibold text-gold hover:underline"
            >
              Read how to rent a car without a credit card in Bucharest
            </Link>
            . Driving in the city for the first time? See our{" "}
            <Link to="/good-to-know" className="font-semibold text-gold hover:underline">
              good to know in Bucharest guide
            </Link>{" "}
            and{" "}
            <Link
              to="/blog/$slug"
              params={{ slug: "car-rental-no-deposit-romania" }}
              className="font-semibold text-gold hover:underline"
            >
              how no-deposit rental works in Romania
            </Link>
            .
          </p>

        </div>
      </section>
    </>
  );
}
